import React, { useEffect, useState } from 'react'
import { MetaTitle, ImageGallery, LoadError } from '../../Components/UI'
import { Button, Row, Col, Typography, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { ProposalInfoCard } from './ProposalInfoCard'
import { DetailsCard } from './DetailsCard'
import { LeftOutlined } from '@ant-design/icons'
import { s } from './style'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { StaticContext } from 'react-router'
import { isEqual } from 'lodash'
import { ROUTES } from '../../Constants'
import {
  useProposalGetProposalQueryQuery,
  useProposalUpdateProposalMutationMutation,
} from './Proposal.generated-types'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { Contacts } from './Contacts'
import { ProposalButtons } from './ProposalButtons'
import { UpdateProposalInput } from '../../types'

const { Text } = Typography

type ProposalProps = RouteComponentProps<
{ proposalUUID: string },
StaticContext,
{ inactive: boolean }
>

const Proposal: React.FC<ProposalProps> = ({
  history,
  match: {
    params: { proposalUUID },
  },
  location,
}) => {
  const { t } = useTranslation()
  const [canEdit, setCanEdit] = useState(false)
  const [editing, setEditing] = useState(false)
  const [proposal, setProposal] = useState(null)
  const [proposalUpdated, setProposalUpdated] = useState(false)
  const { authenticated, user, loading } = useGlobalAuth()

  const [{ fetching, error, data }] = useProposalGetProposalQueryQuery({
    variables: { UUIDs: [proposalUUID] },
  })

  const [
    { fetching: updatingProposal, error: updateError, data: updatedProposal },
    updateProposal,
  ] = useProposalUpdateProposalMutationMutation()

  useEffect(() => window.scrollTo(0, 0), [])

  useEffect(() => {
    if (!loading && authenticated && user.roles.includes('account_manager')) {
      setCanEdit(true)
    } else {
      setCanEdit(false)
    }
  }, [authenticated, user, loading])

  useEffect(() => {
    if (data?.getProposalsByUUIDs[0]) {
      setProposal(data.getProposalsByUUIDs[0])
    }
  }, [data])

  useEffect(() => {
    if (updatedProposal && isEqual(updatedProposal.updateProposal, proposal)) {
      setProposalUpdated(true)
    } else {
      setProposalUpdated(false)
    }
  }, [proposal, updatedProposal])

  if (error || updateError || fetching || proposal == null) {
    return <LoadError load={fetching || proposal == null} error={error} />
  }

  const {
    status,
    event,
    space,
    space: { spacePhotos },
  } = proposal

  const onGoToAllProposals = (): void =>
    history.push({
      pathname: `${ROUTES.ALL_PROPOSALS}/${event.UUID}`,
    })

  const goToConfirmation = (): void =>
    history.push({
      pathname: ROUTES.CONFIRMATION,
    })

  const handleStartEditing = (): void => {
    if (canEdit) {
      setEditing(true)
    }
  }

  const handleUpdateProposal = (): void => {
    const {
      UUID,
      baseCostDescription,
      baseCostTotal,
      extraCostItems,
      currency,
      message,
      fromDateTime,
      toDateTime,
      attendees,
    } = proposal

    const proposalInput: UpdateProposalInput = {
      UUID,
      baseCostDescription,
      baseCostTotal,
      extraCostItems,
      currency,
      message,
      fromDateTime,
      toDateTime,
      attendees,
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    proposalInput.extraCostItems = proposal.extraCostItems.map(({ __typename, ...rest }) => {
      return rest
    })
    proposalInput.documentFilePaths = proposal.documents.map(({ s3Key }) => s3Key)

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updateProposal({ input: proposalInput })
  }

  const imageUrls = []

  if (spacePhotos && spacePhotos.length > 0) {
    spacePhotos.forEach((p) => imageUrls.push(p.url))
  }

  const isRequestedToBooked = status === 'RequestedToBook'
  const inactive = isRequestedToBooked || location?.state?.inactive

  const ImageCarousel = (): JSX.Element => (
    <section style={{ position: 'relative' }}>
      <section style={{ width: '100%' }}>
        <ImageGallery {...{ imageUrls }} />
      </section>
      {isRequestedToBooked && (
        <Tag color='blue' style={s.absolute}>
          {t('label.requestedToBook', 'Requested to book')}
        </Tag>
      )}
    </section>
  )

  return (
    <div style={s.main}>
      <MetaTitle
        title={`${space?.venue?.name || ''} ${t(
          'singleProposal.metaTitle.active',
          ' Proposals - HeadBox'
        )}`}
      />
      <Row gutter={[16, 16]} style={s.autoMargin}>
        <Button type='link' onClick={() => onGoToAllProposals()}>
          <LeftOutlined />
          <Text style={s.returnText}>
            {t('singleProposal.viewAllResponses', 'View all responses')}
          </Text>
        </Button>
        <Col span={24}>
          <ImageCarousel />
        </Col>
        <Col span={16}>
          <ProposalInfoCard proposal={proposal} editing={editing} setProposal={setProposal} />
        </Col>
        <Col span={8} style={s.detailsColumn}>
          <DetailsCard proposal={proposal} editing={editing} setProposal={setProposal} />
          <ProposalButtons
            proposal={proposal}
            goToConfirmation={goToConfirmation}
            inactive={inactive}
            isRequestedToBooked={isRequestedToBooked}
            canEdit={canEdit}
            editing={editing}
            handleStartEditing={handleStartEditing}
            handleUpdateProposal={handleUpdateProposal}
            updatingProposal={updatingProposal}
            proposalUpdated={proposalUpdated}
          />
          {canEdit && <Contacts proposal={proposal} />}
        </Col>
      </Row>
    </div>
  )
}

export default withRouter(Proposal)
